import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { ThemeChoice } from '@till-erp/platform/user-values';

export type Theme = ThemeChoice;
const STORAGE_KEY = 'till-erp.theme';

/**
 * Hell/Dunkel (Change 03.10.2026 Design E · final, Rudolf): Hell ist Standard, auch wenn Windows dunkel eingestellt
 * ist. Angemeldet kommt die Wahl aus dem Profil (Server, je Person gemerkt; ohne eigene Wahl liefert er `light`);
 * vor der Anmeldung und als Zwischenspeicher gilt die letzte Wahl auf diesem Gerät. Keine Option „Wie Windows“.
 */
let theme: Theme = readStored() ?? 'light';
let saver: ((next: Theme) => void) | null = null;
const listeners = new Set<() => void>();

function readStored(): Theme | null {
  try {
    // Altwert `system` (vor Design E · final) gilt als „keine eigene Wahl“ = hell.
    return window.localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : null;
  } catch {
    return null;
  }
}

/**
 * Wechsel ohne Überblenden: Sonst laufen Knöpfe mit `transition-colors` nach dem Laden des Profils noch ~150 ms von
 * Hell nach Dunkel (sichtbares Flackern; der Kontrast-Test in `design-s3-dunkel.spec.ts` maß mitten im Übergang).
 */
function apply() {
  const root = document.documentElement;
  if (root.dataset.theme !== theme) {
    root.dataset.themeSwitching = '';
    root.dataset.theme = theme;
    // Neue Farben jetzt berechnen lassen, solange Übergänge aus sind; danach wieder einschalten.
    void root.offsetHeight;
    setTimeout(() => delete root.dataset.themeSwitching, 1);
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Wahl setzen; `persist` speichert sie zusätzlich im Profil (wenn angemeldet). */
export function setTheme(next: Theme, options: { persist?: boolean } = {}): void {
  theme = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ohne Speicher gilt die Wahl nur bis zum Neuladen.
  }
  apply();
  if (options.persist !== false) saver?.(next);
}

/** App-Hülle: Wahl aus dem Profil übernehmen und Änderungen ins Profil speichern. */
export function useProfileTheme(profileTheme: Theme, save: (next: Theme) => void): void {
  useEffect(() => {
    setTheme(profileTheme, { persist: false });
  }, [profileTheme]);
  useEffect(() => {
    saver = save;
    return () => {
      if (saver === save) saver = null;
    };
  }, [save]);
}

apply();

export function useTheme(): { theme: Theme; toggle: () => void } {
  const current = useSyncExternalStore(subscribe, () => theme);
  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, []);
  return { theme: current, toggle };
}
